import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getApiUrl, API_ENDPOINTS } from '../../config/api';
import './AdminHeader.css';

const AdminHeader = ({ children, onPasswordChange }) => {
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const sidebarRef = useRef(null);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    const isAdminSubdomain = window.location.hostname === 'admin.fishmanhealth.com' || 
                            window.location.hostname === 'admin.fishmanmanhealth.com';
    navigate(isAdminSubdomain ? '/login' : '/admin');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(API_ENDPOINTS.changePassword), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change password');
      }

      setSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowPasswordModal(false);
        setSuccess('');
      }, 2000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const navigateToPage = (path) => {
    const isAdminSubdomain = window.location.hostname === 'admin.fishmanhealth.com' || 
                            window.location.hostname === 'admin.fishmanmanhealth.com';
    const fullPath = isAdminSubdomain ? path : `/admin${path}`;
    navigate(fullPath);
    setShowSettingsDropdown(false);
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const isCurrentPage = (path) => {
    const isAdminSubdomain = window.location.hostname === 'admin.fishmanhealth.com' || 
                            window.location.hostname === 'admin.fishmanmanhealth.com';
    const fullPath = isAdminSubdomain ? path : `/admin${path}`;
    return location.pathname === fullPath;
  };

  return (
    <>
      {/* Mobile Menu Toggle */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle mobile menu"
      >
        {isMobileMenuOpen ? '✕' : '☰'}
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && <div className="mobile-overlay" onClick={() => setIsMobileMenuOpen(false)} />}

      <div className="admin-layout">
        <aside 
          ref={sidebarRef}
          className={`admin-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}
        >
          <div className="admin-logo-section">
            <img src="/Fish_Logo.jpeg" alt="Fishman Health" className="admin-logo" />
            <h1>Admin Console</h1>
            <button 
              className="mobile-close-button"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>
          
          <nav className="admin-nav">
            <button 
              className={`nav-button ${isCurrentPage('/dashboard') ? 'active' : ''}`}
              onClick={() => navigateToPage('/dashboard')}
            >
              📊 Dashboard
            </button>
            <button 
              className={`nav-button ${isCurrentPage('/analytics') ? 'active' : ''}`}
              onClick={() => navigateToPage('/analytics')}
            >
              📈 Analytics
            </button>
            <button 
              className={`nav-button ${isCurrentPage('/leads') ? 'active' : ''}`}
              onClick={() => navigateToPage('/leads')}
            >
              👥 Leads
            </button>
            <button 
              className={`nav-button ${isCurrentPage('/customers') ? 'active' : ''}`}
              onClick={() => navigateToPage('/customers')}
            >
              🎯 Customers
            </button>
          </nav>

          <div className="admin-user-section">
            <span className="user-greeting">Welcome, {user.name}</span>
            <div className="settings-dropdown" ref={dropdownRef}>
              <button 
                className="settings-button"
                onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              >
                ⚙️
              </button>
              
              {showSettingsDropdown && (
                <div className="dropdown-menu">
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowPasswordModal(true);
                      setShowSettingsDropdown(false);
                    }}
                  >
                    🔒 Change Password
                  </button>
                  <button 
                    className="dropdown-item logout"
                    onClick={handleLogout}
                  >
                    🚪 Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </aside>
        
        <main className="admin-content">
          {children}
        </main>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="password-modal">
            <div className="modal-header">
              <h3>Change Password</h3>
              <button className="close-button" onClick={closePasswordModal}>×</button>
            </div>
            
            <form onSubmit={handlePasswordChange} className="password-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  minLength={8}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              
              <div className="modal-actions">
                <button type="button" onClick={closePasswordModal} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="primary">
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminHeader; 